import React, { useEffect, useState } from 'react';
import { DeviceSizes } from 'shared/enums/DeviceSizes';
import DayIndicator from './DayIndicator';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Flex, useMediaQuery, Box } from '@televet/kibble-ui/build/chakra';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import RangeCalendar from 'shared/components/RangeCalendar';
import useAppointmentGrouping from 'shared/components/SidePanel/components/SchedulerPanel/hooks/useAppointmentGrouping';

const getDaysAhead = (date: Date, numberOfDays?: number): Date[] => {
  const daysAhead = [new Date(date)];
  for (let i = 0; i < (numberOfDays || 14); i++) {
    daysAhead.push(addDays(date, i + 1));
  }

  return daysAhead;
};

export function DateToolbar({
  currentDay,
  setCurrentDay,
  numberOfDays,
}: {
  currentDay: Date;
  setCurrentDay: (d: Date) => void;
  numberOfDays?: number;
}): JSX.Element {
  const [daysAhead, setDaysAhead] = useState(getDaysAhead(currentDay, numberOfDays));
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMobileWidth] = useMediaQuery('(' + DeviceSizes.MobileMaxWidth + ')');
  const [{ appointmentFilters }, { updateAppointmentFilterDateRange }] = useAppointmentGrouping();

  useEffect(() => {
    if (currentDay < daysAhead[0] || currentDay > daysAhead[daysAhead.length - 1]) {
      setDaysAhead(getDaysAhead(currentDay, numberOfDays));
    }
  }, [currentDay, setDaysAhead, daysAhead, numberOfDays]);

  const onMobileDateRangeChanged = (startDate: Date | null, endDate: Date | null): void => {
    if (startDate && endDate) {
      updateAppointmentFilterDateRange(startDate, endDate);
      setCurrentDay(startDate);
    }
  };

  const onMenuClick = (): void => setIsCalendarOpen((current) => !current);
  const onDayClick = (date: Date): void => setCurrentDay(date);

  return (
    <Flex w="100%" borderBottomWidth="1px" h={isMobileWidth ? '54px' : '74px'}>
      <Flex
        onClick={onMenuClick}
        align="center"
        justify="space-between"
        borderRightWidth="1px"
        px={2}
        py={1}
        minW="130px"
        {...(isMobileWidth && { cursor: 'pointer', minW: '125px' })}
      >
        <Flex flexDir="column">
          <Text fontWeight="semibold" size="lg">
            {format(currentDay, 'EEEE')}
          </Text>
          <Text size="xs">{format(currentDay, 'MMMM do')}</Text>
        </Flex>

        {isMobileWidth && <Icon name="chevronDown" size="sm" />}
      </Flex>

      <Flex
        w="auto"
        overflowX="auto"
        scrollBehavior="smooth"
        align="center"
        px={isMobileWidth ? 0 : 2}
        gap={isMobileWidth ? 0 : 4}
        sx={{
          '&::-webkit-scrollbar': {
            h: 0,
            w: '7px',
          },
          '&::-webkit-scrollbar-track': {
            my: '5px',
            mx: 0,
          },
          '&::-webkit-scrollbar-thumb': {
            opacity: 0.25,
            borderRadius: 'md',
            boxShadow: 'base',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            opacity: 0.35,
          },
        }}
      >
        {daysAhead.map((date: Date, index: number) => {
          return (
            <DayIndicator
              key={`${date.toString()}-${index}}`}
              dayLabel={format(date, 'EEE')}
              dayNumber={format(date, 'dd')}
              date={date}
              currentDay={currentDay}
              onDayClick={onDayClick}
            />
          );
        })}
      </Flex>

      {isMobileWidth && isCalendarOpen && (
        <Box
          bgColor="background.default"
          maxW="375px"
          position="absolute"
          top="54px"
          transition="all 0.7s"
          w="100%"
          zIndex="popover"
        >
          <RangeCalendar
            startDate={appointmentFilters.startDate}
            endDate={appointmentFilters.endDate}
            currentDay={currentDay}
            minBookingDays={appointmentFilters.daysAheadInterval}
            onDateChange={onMobileDateRangeChanged}
            exactMinBookingDays={true}
          />
        </Box>
      )}
    </Flex>
  );
}
